import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { getHeaders } from './api';

// Empty string = relative URLs (production via nginx proxy).
// Only fall back to direct backend URL if env var is truly undefined.
const API_URL = process.env.REACT_APP_API_URL != null ? process.env.REACT_APP_API_URL : 'https://localhost:8443';

// SockJS transport for the STOMP CONNECT. The backend registers /ws with
// `.withSockJS()` (the raw WebSocket endpoint lives at /ws/raw), so the browser
// MUST connect via SockJS — a raw `brokerURL` WebSocket to /ws is rejected by
// the SockJS handshake with HTTP 400. SockJS speaks http(s), not ws(s), so we
// do NOT swap the scheme; a relative '/ws' resolves against the page origin.
// Shared by all three clients below — one transport, no per-client copies.
const sockJsFactory = () => new SockJS(`${API_URL.replace(/\/api\/v1$/, '')}/ws`);

/**
 * Build a fresh STOMP CONNECT auth header before each (re)connect.
 *
 * Routes through the shared getHeaders() (services/api.js) so the bearer is
 * refreshed via the single-flight refreshAccessToken — exactly like every
 * fetch — instead of a token captured once at client-construction time that
 * STOMP's auto-reconnect would then reuse stale (or null, post-SEC-25 reload)
 * forever. The HttpOnly session cookie remains the PRIMARY auth on the WS
 * upgrade request; this header is the defensive second channel, kept current.
 *
 * Wired via `client.beforeConnect` (stompjs awaits it before every connect),
 * so all three clients below share one auth path — no per-client duplication.
 */
const freshConnectHeaders = async () => {
  try {
    const headers = await getHeaders();
    return headers.Authorization ? { Authorization: headers.Authorization } : {};
  } catch (_) {
    return {}; // cookie auth still applies on the upgrade request
  }
};

let _client = null;
let _connectedHandlers = new Map();

export const connectWebSocket = (agentId, handlers = {}) => {
  // SEC-25 Phase 2: auth rides on the HttpOnly session cookie (auto-sent on the
  // same-origin upgrade) + the STOMP CONNECT Authorization header (beforeConnect),
  // not a leaky `?token=` query param. SockJS transport — see sockJsFactory.
  const client = new Client({
    webSocketFactory: sockJsFactory,
    debug: (str) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[stomp]', str);
      }
    },
    reconnectDelay: 5000,
    heartbeatIncoming: 4000,
    heartbeatOutgoing: 4000,
  });

  // Refresh the bearer before every (re)connect (shared path) — see freshConnectHeaders.
  client.beforeConnect = async () => { client.connectHeaders = await freshConnectHeaders(); };

  client.onConnect = (frame) => {
    console.log('[ws] Connected:', frame);

    subscribe(client, `/topic/executions/${agentId}`, (msg) => {
      if (handlers.onExecution) handlers.onExecution(msg);
    });

    subscribe(client, `/topic/lifecycle/${agentId}`, (msg) => {
      if (handlers.onLifecycle) handlers.onLifecycle(msg);
    });

    subscribe(client, `/topic/agent-sync/${agentId}`, (msg) => {
      if (handlers.onSync) handlers.onSync(msg);
    });

    if (handlers.onConnect) handlers.onConnect(frame);
  };

  client.onStompError = (frame) => {
    console.error('[ws] STOMP error:', frame);
    if (handlers.onError) handlers.onError(frame);
  };

  client.onDisconnect = (frame) => {
    console.log('[ws] Disconnected:', frame);
    if (handlers.onDisconnect) handlers.onDisconnect(frame);
  };

  _client = client;
  _connectedHandlers.set(agentId, handlers);

  client.activate();

  return {
    disconnect: () => {
      if (client.active) {
        client.deactivate();
      }
      _connectedHandlers.delete(agentId);
    },
    publish: (destination, body) => {
      if (client.active) {
        client.publish({ destination, body: JSON.stringify(body) });
      }
    },
  };
};

const subscribe = (client, destination, callback) => {
  if (!client.active) return;

  client.subscribe(destination, (message) => {
    try {
      const body = JSON.parse(message.body);
      callback(body);
    } catch (e) {
      console.error('[ws] Parse error:', e);
      callback(message.body);
    }
  });
};

export const disconnect = () => {
  if (_client && _client.active) {
    _client.deactivate();
  }
  _connectedHandlers.clear();
};

export const publish = (destination, body) => {
  if (_client && _client.active) {
    _client.publish({ destination, body: JSON.stringify(body) });
  }
};

export const isConnected = () => _client && _client.active;

// ─── Global Approval WebSocket ────────────────────────────────
// A separate STOMP client that subscribes to /topic/approvals
// for real-time approval notifications regardless of which agent
// page the user is on.

let _approvalClient = null;
let _approvalSubscriptions = [];

/**
 * Connect to the approval notification channel.
 * @param {Object} handlers - { onApprovalEvent(data) }
 * @returns {{ disconnect: Function }}
 */
export const connectApprovalWebSocket = (handlers = {}) => {
  // Don't double-connect
  if (_approvalClient && _approvalClient.active) {
    return { disconnect: disconnectApprovalWebSocket };
  }

  const client = new Client({
    webSocketFactory: sockJsFactory,
    debug: (str) => {
      if (process.env.NODE_ENV === 'development' && str.includes('approval')) {
        console.log('[stomp:approvals]', str);
      }
    },
    reconnectDelay: 5000,
    heartbeatIncoming: 10000,
    heartbeatOutgoing: 10000,
  });

  // Refresh the bearer before every (re)connect (shared path) — see freshConnectHeaders.
  client.beforeConnect = async () => { client.connectHeaders = await freshConnectHeaders(); };

  client.onConnect = () => {
    console.log('[ws:approvals] Connected');

    // Subscribe to the global approval topic
    const sub = client.subscribe('/topic/approvals', (message) => {
      try {
        const data = JSON.parse(message.body);
        if (handlers.onApprovalEvent) handlers.onApprovalEvent(data);
      } catch (e) {
        console.error('[ws:approvals] Parse error:', e);
      }
    });
    _approvalSubscriptions.push(sub);

    if (handlers.onConnect) handlers.onConnect();
  };

  client.onStompError = (frame) => {
    console.error('[ws:approvals] STOMP error:', frame);
  };

  _approvalClient = client;
  client.activate();

  return { disconnect: disconnectApprovalWebSocket };
};

export const disconnectApprovalWebSocket = () => {
  _approvalSubscriptions.forEach(s => { try { s.unsubscribe(); } catch (_) {} });
  _approvalSubscriptions = [];
  if (_approvalClient && _approvalClient.active) {
    _approvalClient.deactivate();
  }
  _approvalClient = null;
};

// ─── Workflow Execution WebSocket ────────────────────────────────────
// Real-time node execution updates for the canvas. Subscribes to:
//   /topic/workflow-execution/{workflowRunId}  — per-node status changes
//   /topic/enforcement/{workflowRunId}         — DPG scan results, tokenization events
//   /topic/compliance/{workflowRunId}          — compliance score changes, probation events

let _workflowClient = null;
let _workflowSubscriptions = [];

/**
 * Connect to workflow execution channels for real-time canvas updates.
 * @param {string} workflowRunId - The workflow run to subscribe to
 * @param {Object} handlers - {
 *   onNodeUpdate(data),          — node status change (pending→running→completed→failed)
 *   onEnforcementEvent(data),    — DPG scan result, tokenization, policy gate decision
 *   onComplianceEvent(data),     — compliance score change, probation trigger
 *   onWorkflowComplete(data),    — workflow execution finished
 *   onConnect(frame)
 * }
 * @returns {{ disconnect: Function }}
 */
export const connectWorkflowWebSocket = (workflowRunId, handlers = {}) => {
  // Disconnect any previous workflow subscription
  disconnectWorkflowWebSocket();

  const client = new Client({
    webSocketFactory: sockJsFactory,
    debug: (str) => {
      if (process.env.NODE_ENV === 'development' && (str.includes('workflow') || str.includes('enforcement'))) {
        console.log('[stomp:workflow]', str);
      }
    },
    reconnectDelay: 3000,
    heartbeatIncoming: 4000,
    heartbeatOutgoing: 4000,
  });

  // Refresh the bearer before every (re)connect (shared path) — see freshConnectHeaders.
  client.beforeConnect = async () => { client.connectHeaders = await freshConnectHeaders(); };

  client.onConnect = () => {
    console.log(`[ws:workflow] Connected to run ${workflowRunId}`);

    // Node execution updates (per-node status changes with timing data)
    const sub1 = client.subscribe(`/topic/workflow-execution/${workflowRunId}`, (message) => {
      try {
        const data = JSON.parse(message.body);
        if (data.type === 'WORKFLOW_COMPLETE' && handlers.onWorkflowComplete) {
          handlers.onWorkflowComplete(data);
        } else if (handlers.onNodeUpdate) {
          handlers.onNodeUpdate(data);
        }
      } catch (e) {
        console.error('[ws:workflow] Parse error:', e);
      }
    });
    _workflowSubscriptions.push(sub1);

    // Enforcement events (DPG scans, tokenization, policy decisions)
    const sub2 = client.subscribe(`/topic/enforcement/${workflowRunId}`, (message) => {
      try {
        const data = JSON.parse(message.body);
        if (handlers.onEnforcementEvent) handlers.onEnforcementEvent(data);
      } catch (e) {
        console.error('[ws:enforcement] Parse error:', e);
      }
    });
    _workflowSubscriptions.push(sub2);

    // Compliance events (score changes, autonomy transitions, probation)
    const sub3 = client.subscribe(`/topic/compliance/${workflowRunId}`, (message) => {
      try {
        const data = JSON.parse(message.body);
        if (handlers.onComplianceEvent) handlers.onComplianceEvent(data);
      } catch (e) {
        console.error('[ws:compliance] Parse error:', e);
      }
    });
    _workflowSubscriptions.push(sub3);

    if (handlers.onConnect) handlers.onConnect();
  };

  client.onStompError = (frame) => {
    console.error('[ws:workflow] STOMP error:', frame);
  };

  _workflowClient = client;
  client.activate();

  return { disconnect: disconnectWorkflowWebSocket };
};

export const disconnectWorkflowWebSocket = () => {
  _workflowSubscriptions.forEach(s => { try { s.unsubscribe(); } catch (_) {} });
  _workflowSubscriptions = [];
  if (_workflowClient && _workflowClient.active) {
    _workflowClient.deactivate();
  }
  _workflowClient = null;
};
